import IconDownload from '../../../assets/images/icons/icon_download.svg';
import IconNewCircle from '../../../assets/images/icons/icon_new_circle.svg';
import IconUpload from '../../../assets/images/icons/icon_upload.svg';
import { ButtonOptions } from '../../../components/ButtonWithOptions/ButtonWithOptions';
import { CustomType, DataGridColDef } from '../../../utils/types/listItems';

export const BANK_FEEDS_VIEW_KEY = 'BANK_FEEDS_VIEW_KEY';

export enum BankFeedView {
  DASHBOARD = 'Dashboard',
  HYBRID = 'Hybrid',
  DATA_VIEW = 'Data View',
}

export type BankFeedViewType =
  | BankFeedView.DASHBOARD
  | BankFeedView.HYBRID
  | BankFeedView.DATA_VIEW;

export enum BankFeedsTransStatus {
  PENDING = 'Pending',
  CATEGORIZED = 'Categorized',
  EXCLUDED = 'Excluded',
}

export const BankFeedsStatusFilter = [
  { id: 'PENDING', name: 'Pending' },
  { id: 'CATEGORIZED', name: 'Categorized' },
  { id: 'EXCLUDED', name: 'Excluded' },
];

export type BankFeedsStatusType = 'PENDING' | 'CATEGORIZED' | 'EXCLUDED';

export enum BFTInOutflowType {
  INFLOW,
  OUTFLOW,
}

export type JEPostingType = 'DEBIT' | 'CREDIT';

export type BankFeedTransactionType = {
  items?: [
    {
      id?: string;
      date?: string;
      description?: string;
      type?: BFTInOutflowType;
      memo?: string;
      glAccountId?: string;
      glAccountName?: string;
      fundId?: string;
      fundName?: string;
      ledgerId?: string;
      ledgerName?: string;
      memoEntityId?: string;
      memeoEntityName?: string;
      score?: number;
      ruleId?: string;
      ruleName?: string;
      bankAccountId?: string;
      bankAccountName?: string;
      bankAccountNickname?: string;
      amount?: number;
      currency?: string;
      isPlaid?: boolean;
      status?: BankFeedsStatusType;
      journalEntry?: {
        ledgerId?: string;
        memo?: string;
        reference?: string;
        receivableTransactionId?: string;
        lineItems?: [
          {
            id?: string;
            name?: string;
            accountName?: 'Accounts Receivable';
            amount?: number;
            postingType?: JEPostingType;
            description?: string;
            currency?: string;
          }
        ];
      };
    }
  ];
  pageInfo?: {
    pageNumber?: number;
    totalPages?: number;
    pageSize?: number;
    totalItems?: number;
    isPaged?: boolean;
  };
};

export enum BankFeedsFilter {
  BankAccount = 'bankAccount',
  Fund = 'fund',
  Status = 'status',
  DateRange = 'dateRange',
  Account = 'account',
  RuleApplied = 'ruleApplied',
}

export enum AddNewButtonOptions {
  LinkAccount = 'link_account',
  UploadBankFeedTemplate = 'upload_bank_feed_template',
  DownloadBankFeedTemplate = 'download_bank_feed_template',
}

export const NewBankFeedOptions: ButtonOptions[] = [
  {
    label: 'Link New Bank Account',
    id: AddNewButtonOptions.LinkAccount,
    icon: IconNewCircle,
  },
  {
    label: 'Upload Bank Feed Template',
    id: AddNewButtonOptions.UploadBankFeedTemplate,
    icon: IconUpload,
    type: 'upload',
  },
  {
    label: 'Download Bank Feed Template',
    id: AddNewButtonOptions.DownloadBankFeedTemplate,
    icon: IconDownload,
  },
];

export const defaultHeaderList: DataGridColDef[] = [
  {
    field: 'bankAccount',
    headerName: 'Bank Account',
    width: 400,
    hide: false,
    index: 1,
    type: 'string',
    sortable: true,
  },
  {
    field: 'fund',
    headerName: 'Fund',
    hide: false,
    index: 2,
    type: 'string',
    sortable: true,
  },
  {
    field: 'status',
    headerName: 'Status',
    hide: false,
    index: 3,
    type: 'string',
    sortable: true,
  },
  {
    field: 'date',
    headerName: 'Date',
    hide: false,
    index: 4,
    type: 'string',
    sortable: true,
  },
  {
    field: 'inflow',
    headerName: 'Inflow',
    hide: false,
    index: 5,
    type: 'string',
    sortable: true,
  },
  {
    field: 'outflow',
    headerName: 'Outflow',
    hide: false,
    index: 6,
    type: 'string',
    sortable: true,
  },
  {
    field: 'description',
    headerName: 'Description',
    hide: false,
    index: 7,
    type: 'string',
    sortable: false,
  },
  {
    field: 'bankMemo',
    headerName: 'Bank Memo',
    hide: false,
    index: 8,
    type: 'string',
    sortable: false,
  },
  {
    field: 'account',
    headerName: 'Account',
    hide: false,
    index: 9,
    type: 'string',
    sortable: true,
  },
  {
    field: 'ruleApplied',
    headerName: 'Rule Applied',
    hide: false,
    index: 10,
    type: 'string',
    sortable: true,
  },
  {
    field: 'score',
    headerName: 'Score',
    hide: false,
    index: 11,
    type: 'number',
    customType: CustomType.Percent,
    sortable: false,
  },
  {
    field: 'capCallReceived',
    headerName: 'Capital Call Received',
    hide: false,
    index: 12,
    type: 'string',
    sortable: false,
  },
  {
    field: 'action',
    headerName: '',
    hide: false,
    index: 13,
    type: 'action',
    sortable: false,
  },
];

function getRandomInt(min: number, max: number) {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function dataGen(object: { [key: string]: string }, copies: number) {
  const result: { [key: string]: string }[] = [];

  for (let i = 1; i <= copies; i++) {
    const newObj: { [key: string]: string } = {};

    for (const key in object) {
      if (key === 'currencyCode') {
        newObj[key] = object[key];
      } else if (key === 'type') {
        getRandomInt(1, 3) === 1
          ? (newObj[key] = 'INFLOW')
          : (newObj[key] = 'OUTFLOW');
      } else if (key === 'date') {
        newObj[key] = '2024-01-09';
      } else if (key === 'status') {
        const option = getRandomInt(1, 3);

        switch (option) {
          case 1:
            newObj[key] = 'Pending';
            break;
          case 2:
            newObj[key] = 'Categorized';
            break;
          case 3:
            newObj[key] = 'Excluded';
            break;
          default:
            break;
        }
      } else {
        newObj[key] = object[key] + i;
      }
    }
    result.push(newObj);
  }
  return result;
}

export const dataListSim = dataGen(
  {
    id: 'id',
    bankAccount: 'Bank Acct 1',
    fund: 'Fund 1',
    status: 'Status 1',
    date: 'Date 1',
    type: '',
    inflow: '',
    outflow: '',
    description: 'Description 1',
    bankMemo: 'Bank Memo 1',
    account: 'Chart of Accounts 1',
    ruleApplied: 'Rule 1',
    score: '0.88',
    capCallReceived: 'Cap Call 1',
    currencyCode: 'USD',
  },
  50
);
